import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import AmplifyApiService, { PaginatedResult } from '../../services/AmplifyApiService';
import {
  CountryCodeToLabelMap,
  CountryCodeToRegionCodeMap,
  RegionCodeToLabelMap,
} from '../../shared/helpers/country-region-helpers';
import removeUndefinedQueryParams from '../../shared/helpers/remove-undefined-query-params';
import IAmplifyApiConfig from '../../shared/interfaces/IAmplifyApiConfig';
import { IProduct } from '../../shared/interfaces/IProduct';

export type GetProductsProps = {
  country?: string;
  region?: string;
  search?: string;
  tagId?: string;
  permittedCompanyId?: string;
  permittedBpn?: string;
  size?: number;
  skip?: number;
  sort?: string;
  hasActiveFootprint?: boolean;
};

type AddProductProps = {
  productId: string;
  productName: string;
  region: string;
  country?: string;
  description?: string;
};

type AddProductResponse = {
  id: number;
};

export type UpdatePcfValuesResponse = {
  outdatedRequestCount: number;
  updatedRequestCount: number;
};

type UpdateProductProps = {
  id: string;
  productId?: string;
  productName?: string;
  location?: string;
  region?: string | null;
  country?: string | null;
  description?: string;
};

type UpdateProductResponse = Omit<IProduct, 'pcf'>;

export enum SharePcfType {
  PACIFIC = 'pacific',
  MANUAL = 'manual',
}

export type SharePcfProps = {
  sharePcfType: SharePcfType;
  productId: number;
  companyId?: number;
  companyName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
};

export type ExportProductsResponse = {
  fileUrl: string;
};

export type SharePcfResponse = {
  sharePcfType: SharePcfType;
  isExistingUser: boolean;
};

const amplifyApiService = new AmplifyApiService();

const BASE_PATH_COMPONENTS = 'components';
const BASE_PATH = 'products';

export const useGetProductById = (
  productId: string,
  options: UseQueryOptions<IProduct, Error> = {},
): UseQueryResult<IProduct, Error> => {
  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: `${BASE_PATH}/${productId}`,
    options: {},
  };

  const query = useQuery<IProduct, Error>({
    queryKey: [BASE_PATH, productId],
    queryFn: async (): Promise<IProduct> => {
      const response = await amplifyApiService.get<IProduct>(amplifyApiConfig);

      return response;
    },
    ...options,
  });

  return query;
};

export const useGetPaginatedProducts = (
  props: GetProductsProps = {},
  options: UseQueryOptions<PaginatedResult<IProduct[]>, Error> = {},
): UseQueryResult<PaginatedResult<IProduct[]>, Error> => {
  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: BASE_PATH,
    options: {
      queryParams: removeUndefinedQueryParams(props),
    },
  };

  const query = useQuery<PaginatedResult<IProduct[]>, Error>({
    queryKey: [BASE_PATH, props],
    queryFn: async (): Promise<PaginatedResult<IProduct[]>> => {
      const response = await amplifyApiService.paginatedGet<IProduct[]>(amplifyApiConfig);

      return response;
    },
    ...options,
  });

  return query;
};

export const useGetProducts = (
  filters: GetProductsProps = {},
  options: UseQueryOptions<IProduct[], Error> = {},
): UseQueryResult<IProduct[]> => {
  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: BASE_PATH,
    options: {
      queryParams: removeUndefinedQueryParams(filters),
    },
  };

  const query = useQuery<IProduct[], Error>({
    queryKey: [BASE_PATH, filters],
    queryFn: async (): Promise<IProduct[]> => {
      const response = await amplifyApiService.get<IProduct[]>(amplifyApiConfig);

      return response;
    },
    ...options,
  });

  return query;
};

export const useAddProduct = (
  options: UseMutationOptions<AddProductResponse, Error, AddProductProps> = {},
): UseMutationResult<AddProductResponse, Error, AddProductProps> => {
  const mutation = useMutation<AddProductResponse, Error, AddProductProps>({
    mutationFn: async (body: AddProductProps) => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: BASE_PATH,
        options: { body },
      };

      return await amplifyApiService.post<AddProductProps, AddProductResponse>(amplifyApiConfig);
    },
    ...options,
  });

  return mutation;
};

export const useUpdateProductDetails = (
  options: UseMutationOptions<UpdateProductResponse, Error, UpdateProductProps> = {},
): UseMutationResult<UpdateProductResponse, Error, UpdateProductProps> => {
  const mutation = useMutation<UpdateProductResponse, Error, UpdateProductProps>({
    mutationFn: async ({ id, ...body }: UpdateProductProps) => {
      const { location, ...requestBody } = body;

      // Handle location and region values in the request body
      if (location) {
        if (CountryCodeToLabelMap[location]) {
          requestBody.country = location;
          requestBody.region = CountryCodeToRegionCodeMap[location];
        } else if (RegionCodeToLabelMap[location]) {
          requestBody.region = location;
          requestBody.country = null;
        }
      }

      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: `${BASE_PATH}/${id}`,
        options: {
          body: requestBody,
        },
      };

      return await amplifyApiService.put<Partial<UpdateProductProps>, UpdateProductResponse>(
        amplifyApiConfig,
      );
    },
    ...options,
  });

  return mutation;
};

export const useSharePcf = (
  options: UseMutationOptions<SharePcfResponse, Error, SharePcfProps> = {},
): UseMutationResult<SharePcfResponse, Error, SharePcfProps> => {
  const mutation = useMutation<SharePcfResponse, Error, SharePcfProps>({
    mutationFn: async ({ productId, ...body }: SharePcfProps) => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: `${BASE_PATH}/${productId}/share-pcf`,
        options: {
          body: body,
        },
      };

      return await amplifyApiService.post<Partial<SharePcfProps>, SharePcfResponse>(
        amplifyApiConfig,
      );
    },

    ...options,
  });

  return mutation;
};

export const useExportProducts = (
  options: UseMutationOptions<ExportProductsResponse, Error, void> = {},
): UseMutationResult<ExportProductsResponse, Error, void> => {
  const mutation = useMutation<ExportProductsResponse, Error, void>({
    mutationFn: async (): Promise<ExportProductsResponse> => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: `${BASE_PATH_COMPONENTS}/export-components`,
      };

      return await amplifyApiService.post<void, ExportProductsResponse>(amplifyApiConfig);
    },

    ...options,
  });

  return mutation;
};

export const useUpdatePcfValues = (
  options: UseMutationOptions<UpdatePcfValuesResponse, Error, void> = {},
): UseMutationResult<UpdatePcfValuesResponse, Error, void> => {
  const mutation = useMutation<UpdatePcfValuesResponse, Error, void>({
    mutationFn: async (): Promise<UpdatePcfValuesResponse> => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: `${BASE_PATH_COMPONENTS}/update-pcf-values`,
      };

      const result = await amplifyApiService.post<void, UpdatePcfValuesResponse>(amplifyApiConfig);

      return result;
    },
    ...options,
  });

  return mutation;
};
